<template>
  <section class="process-section">
    <div class="section-container">

      <div class="header">
        <div class="tag">• INVESTIGATION PROTOCOL</div>
        <h2 class="title">How We <span class="gradient">Recover Your Funds</span></h2>

        <div class="divider-container">
          <div class="line"></div>
          <span class="square-icon">[■]</span>
          <div class="line"></div>
        </div>
      </div>

      <div class="steps-wrapper">
        <div class="connect-line"></div>
        <div class="steps-grid">
          <div v-for="(step, index) in steps" :key="index" class="step-item">
            <div class="icon-circle">
              <span class="icon">{{ step.icon }}</span>
            </div>
            <div class="step-num">STEP #{{ (index + 1).toString().padStart(2, '0') }}</div>
            <h3 class="step-title">{{ step.title }}</h3>
            <p class="step-desc">{{ step.desc }}</p>
          </div>
        </div>
      </div>

      <div class="stats-bar">
        <div v-for="(stat, index) in stats" :key="index" class="stat-item">
          <div class="num">{{ stat.value }}</div>
          <div class="label">{{ stat.label }}</div>
        </div>
      </div>

    </div>
  </section>
</template>

<script>
export default {
  name: 'ProcessSection',
  data() {
    return {
      steps: [
        {
          icon: '📋',
          title: 'Case Submission',
          desc: 'Submit your case details securely. Our team reviews your situation within 24 hours and assesses recovery viability at no cost.'
        },
        {
          icon: '🔍',
          title: 'Blockchain Investigation',
          desc: 'Our forensic analysts trace stolen assets across wallets, chains, and exchanges using advanced blockchain intelligence tools and analytics platforms.'
        },
        {
          icon: '⚖️',
          title: 'Legal Action',
          desc: 'Compiled evidence is submitted to appropriate legal and enforcement channels, initiating formal recovery proceedings through regulated authorities.'
        },
        {
          icon: '✅',
          title: 'Recovery & Closure',
          desc: 'Assets are secured and returned. You receive a full case report and post-recovery security consultation to protect against future threats.'
        }
      ],
      stats: [
        { value: '5,000+', label: 'CASES REVIEWED' },
        { value: '$1.83M', label: 'FUNDS RECOVERED' },
        { value: '94.7%', label: 'SUCCESS RATE' },
        { value: '40+', label: 'COUNTRIES SERVED' },
        { value: '24/7', label: 'LIVE MONITORING' },
        { value: '10+', label: 'ACTIVE SERVICES' }
      ]
    };
  }
}
</script>

<style scoped>
.process-section {
  background-color: #070b14;
  padding: 60px 5%;
  color: #e2e8f0;
}

.section-container {
  max-width: 1200px;
  margin: 0 auto;
}

.header {
  position: relative;
  margin-bottom: 60px;
}

.tag {
  color: #4fd1c5;
  font-size: 0.75rem;
  letter-spacing: 2px;
  margin-bottom: 10px;
  font-weight: 500;
}

.title {
  font-size: 2.6rem;
  font-weight: 700;
  margin: 0 0 20px;
}

.gradient {
  background: linear-gradient(90deg, #4fd1c5, #8be9fd);
  -webkit-background-clip: text;
  color: transparent;
}

.divider-container {
  display: flex;
  align-items: center;
  width: 100%;
  margin-top: 10px;
}

.divider-container .line {
  flex: 1;
  height: 1px;
  background-color: rgba(79, 209, 197, 0.2);
}

.divider-container .square-icon {
  color: #4fd1c5;
  font-size: 0.8rem;
  font-family: monospace;
  margin: 0 15px;
}

.steps-wrapper {
  position: relative;
  margin-bottom: 80px;
}

.connect-line {
  position: absolute;
  top: 35px;
  left: 12.5%;
  right: 12.5%;
  height: 2px;
  background-color: rgba(79, 209, 197, 0.5);
  z-index: 1;
}

.steps-grid {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}

.step-item {
  text-align: center;
}

.icon-circle {
  width: 70px;
  height: 70px;
  background-color: #0a101f;
  border: 1px solid rgba(79, 209, 197, 0.5);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto 15px;
}

.icon {
  font-size: 1.8rem;
}

.step-num {
  color: #4fd1c5;
  font-family: monospace;
  font-size: 0.8rem;
  letter-spacing: 1px;
  margin-bottom: 10px;
  font-weight: 600;
}

.step-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 15px;
  color: #fff;
}

.step-desc {
  color: #94a3b8;
  font-size: 0.9rem;
  line-height: 1.6;
  max-width: 280px;
  margin: 0 auto;
}

.stats-bar {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  border: 1px solid rgba(79, 209, 197, 0.2);
  border-radius: 8px;
  background: rgba(13, 17, 23, 0.6);
  overflow: hidden;
}

.stat-item {
  text-align: center;
  padding: 20px 10px;
  border-right: 1px solid rgba(79, 209, 197, 0.2);
}

.stat-item:last-child {
  border-right: none;
}

.num {
  color: #4fd1c5;
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 5px;
}

.label {
  color: #94a3b8;
  font-size: 0.7rem;
  letter-spacing: 1px;
}

@media (max-width: 900px) {
  .steps-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 40px 30px;
  }

  .connect-line {
    display: none;
  }

  .stats-bar {
    grid-template-columns: repeat(3, 1fr);
  }

  .stat-item:nth-child(3n) {
    border-right: none;
  }

  .stat-item:nth-child(-n+3) {
    border-bottom: 1px solid rgba(79, 209, 197, 0.2);
  }
}

@media (max-width: 600px) {
  .title {
    font-size: 2rem;
  }

  .steps-grid {
    grid-template-columns: 1fr;
  }

  .stats-bar {
    grid-template-columns: repeat(2, 1fr);
  }

  .stat-item {
    border-bottom: 1px solid rgba(79, 209, 197, 0.2);
  }

  .stat-item:nth-child(even) {
    border-right: none;
  }
}
</style>